/**
 * Minimap.ts - World overview minimap for the bottom-right corner
 * Shows player position on the 64x64 tile world map
 */

import { TerrainType, TERRAIN_COLORS } from '../data/TilesetConfig';
import { LOCATIONS, LocationType } from '../data/WorldMapData';

export interface MinimapConfig {
  /** Minimap size in pixels (default: 64) */
  size?: number;
  /** Padding from screen edge (default: 4) */
  padding?: number;
  /** Show location markers (default: true) */
  showLocations?: boolean;
  /** Show viewport rectangle (default: false) */
  showViewport?: boolean;
  /** Opacity of the minimap (default: 0.9) */
  opacity?: number;
  /** Enable fog of war - unexplored areas are hidden (default: true) */
  fogOfWar?: boolean;
  /** Reveal radius around player in tiles (default: 3) */
  revealRadius?: number;
}

export class Minimap {
  // Dimensions
  private readonly size: number;
  private readonly padding: number;

  // Position (calculated for bottom-right corner of 256x240 screen)
  private screenX: number;
  private screenY: number;

  // Configuration
  private showLocations: boolean;
  private showViewport: boolean;
  private opacity: number;
  private fogOfWar: boolean;
  private revealRadius: number;

  // Cached terrain image
  private terrainCache: OffscreenCanvas | null = null;
  private cacheValid: boolean = false;

  // World data reference
  private worldTiles: TerrainType[][] | null = null;
  private worldWidth: number = 64;
  private worldHeight: number = 64;

  // Fog of war - tracks which tiles have been explored
  private exploredTiles: boolean[][] = [];
  private fogCache: OffscreenCanvas | null = null;
  private fogCacheValid: boolean = false;

  // Visibility state
  private visible: boolean = true;

  // Animation
  private playerBlinkTimer: number = 0;
  private playerBlinkOn: boolean = true;

  constructor(config: MinimapConfig = {}) {
    this.size = config.size ?? 64;
    this.padding = config.padding ?? 4;
    this.showLocations = config.showLocations ?? true;
    this.showViewport = config.showViewport ?? false;
    this.opacity = config.opacity ?? 0.9;
    this.fogOfWar = config.fogOfWar ?? true;
    this.revealRadius = config.revealRadius ?? 4;

    // Calculate screen position (bottom-right)
    this.screenX = 256 - this.size - this.padding;
    this.screenY = 240 - this.size - this.padding;
  }

  /**
   * Set the world tile data for the minimap
   */
  setWorldData(tiles: TerrainType[][], width: number, height: number): void {
    this.worldTiles = tiles;
    this.worldWidth = width;
    this.worldHeight = height;
    this.invalidateCache();

    // Initialize explored tiles array (all unexplored by default)
    this.exploredTiles = [];
    for (let y = 0; y < height; y++) {
      this.exploredTiles[y] = new Array(width).fill(false);
    }
    this.fogCacheValid = false;
  }

  /**
   * Invalidate the terrain cache (call when world changes)
   */
  invalidateCache(): void {
    this.cacheValid = false;
  }

  /**
   * Reveal tiles around a position (call when player moves)
   */
  revealArea(centerX: number, centerY: number): void {
    if (!this.fogOfWar) return;

    let revealed = false;
    const radius = this.revealRadius;

    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        const x = centerX + dx;
        const y = centerY + dy;

        // Check bounds
        if (x < 0 || x >= this.worldWidth || y < 0 || y >= this.worldHeight) continue;

        // Use circular reveal (distance check)
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance <= radius) {
          if (!this.exploredTiles[y]?.[x]) {
            if (this.exploredTiles[y]) {
              this.exploredTiles[y][x] = true;
              revealed = true;
            }
          }
        }
      }
    }

    // Invalidate fog cache if new tiles were revealed
    if (revealed) {
      this.fogCacheValid = false;
    }
  }

  /**
   * Check if a tile has been explored
   */
  isExplored(x: number, y: number): boolean {
    return this.exploredTiles[y]?.[x] ?? false;
  }

  /**
   * Reveal the entire map (cheat/debug)
   */
  revealAll(): void {
    for (let y = 0; y < this.worldHeight; y++) {
      if (this.exploredTiles[y]) {
        this.exploredTiles[y].fill(true);
      }
    }
    this.fogCacheValid = false;
  }

  /**
   * Get explored data for saving
   */
  getExploredData(): boolean[][] {
    return this.exploredTiles;
  }

  /**
   * Load explored data from save
   */
  loadExploredData(data: boolean[][]): void {
    if (data && data.length === this.worldHeight) {
      this.exploredTiles = data;
      this.fogCacheValid = false;
    }
  }

  /**
   * Toggle minimap visibility
   */
  toggle(): void {
    this.visible = !this.visible;
  }

  /**
   * Set minimap visibility
   */
  setVisible(visible: boolean): void {
    this.visible = visible;
  }

  /**
   * Check if minimap is visible
   */
  isVisible(): boolean {
    return this.visible;
  }

  /**
   * Update minimap animations
   */
  update(deltaTime: number): void {
    // Player marker blink animation
    this.playerBlinkTimer += deltaTime;
    if (this.playerBlinkTimer >= 400) {
      this.playerBlinkTimer = 0;
      this.playerBlinkOn = !this.playerBlinkOn;
    }
  }

  /**
   * Main render method
   */
  render(
    ctx: CanvasRenderingContext2D,
    playerTileX: number,
    playerTileY: number,
    viewportX?: number,
    viewportY?: number,
    viewportWidth?: number,
    viewportHeight?: number
  ): void {
    if (!this.visible || !this.worldTiles) return;

    // Reveal area around player
    if (this.fogOfWar) {
      this.revealArea(playerTileX, playerTileY);
    }

    // Save context state
    ctx.save();

    // Apply opacity
    ctx.globalAlpha = this.opacity;

    // Draw background
    this.drawBackground(ctx);

    // Draw terrain (cached)
    this.drawTerrain(ctx);

    // Draw fog of war overlay
    if (this.fogOfWar) {
      this.drawFog(ctx);
    }

    // Draw location markers (only for explored areas)
    if (this.showLocations) {
      this.drawLocations(ctx);
    }

    // Draw viewport rectangle
    if (this.showViewport && viewportX !== undefined) {
      this.drawViewport(ctx, viewportX, viewportY!, viewportWidth!, viewportHeight!);
    }

    // Draw player position (always on top)
    ctx.globalAlpha = 1.0; // Player marker fully opaque
    this.drawPlayer(ctx, playerTileX, playerTileY);

    // Draw border
    this.drawBorder(ctx);

    // Restore context state
    ctx.restore();
  }

  /**
   * Draw dark background
   */
  private drawBackground(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = '#0a0a1a';
    ctx.fillRect(this.screenX, this.screenY, this.size, this.size);
  }

  /**
   * Draw terrain layer (cached for performance)
   */
  private drawTerrain(ctx: CanvasRenderingContext2D): void {
    if (!this.worldTiles) return;

    // Build cache if needed
    if (!this.cacheValid || !this.terrainCache) {
      this.buildTerrainCache();
    }

    // Draw cached terrain
    if (this.terrainCache) {
      ctx.drawImage(this.terrainCache, this.screenX, this.screenY);
    }
  }

  /**
   * Build the terrain cache image
   */
  private buildTerrainCache(): void {
    if (!this.worldTiles) return;

    // Create offscreen canvas for caching
    this.terrainCache = new OffscreenCanvas(this.size, this.size);
    const cacheCtx = this.terrainCache.getContext('2d');
    if (!cacheCtx) return;

    // Calculate scale (world tiles to minimap pixels)
    const scaleX = this.size / this.worldWidth;
    const scaleY = this.size / this.worldHeight;

    // Draw each tile as a colored pixel
    for (let y = 0; y < this.worldHeight; y++) {
      const row = this.worldTiles[y];
      if (!row) continue;

      for (let x = 0; x < this.worldWidth; x++) {
        const terrain = row[x];
        if (terrain === undefined) continue;

        const color = this.getTerrainColor(terrain);
        cacheCtx.fillStyle = color;

        // Calculate pixel position
        const px = Math.floor(x * scaleX);
        const py = Math.floor(y * scaleY);
        const pw = Math.max(1, Math.ceil(scaleX));
        const ph = Math.max(1, Math.ceil(scaleY));

        cacheCtx.fillRect(px, py, pw, ph);
      }
    }

    this.cacheValid = true;
  }

  /**
   * Get color for terrain type
   */
  private getTerrainColor(terrain: TerrainType): string {
    // Use slightly adjusted colors for better minimap visibility
    const minimapColors: Partial<Record<TerrainType, string>> = {
      [TerrainType.OCEAN]: '#0a3060',
      [TerrainType.DEEP_WATER]: '#082050',
      [TerrainType.GRASS]: '#1a7a1a',
      [TerrainType.FOREST]: '#0d4d0d',
      [TerrainType.MOUNTAIN]: '#606060',
      [TerrainType.DESERT]: '#c0a040',
      [TerrainType.SWAMP]: '#405030',
      [TerrainType.RIVER]: '#1050a0',
      [TerrainType.ROAD]: '#806040',
      [TerrainType.SHORE]: '#80a070',
      [TerrainType.SNOW]: '#d0e0f0',
      [TerrainType.LAVA]: '#c03000',
    };

    return minimapColors[terrain] ?? TERRAIN_COLORS[terrain] ?? '#ff00ff';
  }

  /**
   * Draw fog of war overlay (darkens unexplored areas)
   */
  private drawFog(ctx: CanvasRenderingContext2D): void {
    // Build fog cache if needed
    if (!this.fogCacheValid || !this.fogCache) {
      this.buildFogCache();
    }

    // Draw cached fog
    if (this.fogCache) {
      ctx.drawImage(this.fogCache, this.screenX, this.screenY);
    }
  }

  /**
   * Build the fog of war cache image
   */
  private buildFogCache(): void {
    // Create offscreen canvas for fog
    this.fogCache = new OffscreenCanvas(this.size, this.size);
    const fogCtx = this.fogCache.getContext('2d');
    if (!fogCtx) return;

    // Clear to transparent
    fogCtx.clearRect(0, 0, this.size, this.size);

    // Calculate scale
    const scaleX = this.size / this.worldWidth;
    const scaleY = this.size / this.worldHeight;

    // Draw fog over unexplored tiles
    for (let y = 0; y < this.worldHeight; y++) {
      for (let x = 0; x < this.worldWidth; x++) {
        if (!this.exploredTiles[y]?.[x]) {
          // Calculate pixel position
          const px = Math.floor(x * scaleX);
          const py = Math.floor(y * scaleY);
          const pw = Math.max(1, Math.ceil(scaleX));
          const ph = Math.max(1, Math.ceil(scaleY));

          // Dark fog color with slight variation for texture
          const darkness = 0.85 + Math.random() * 0.1;
          fogCtx.fillStyle = `rgba(5, 5, 15, ${darkness})`;
          fogCtx.fillRect(px, py, pw, ph);
        } else {
          // Check for edge tiles (partially revealed for softer edges)
          const hasUnexploredNeighbor =
            !this.exploredTiles[y - 1]?.[x] ||
            !this.exploredTiles[y + 1]?.[x] ||
            !this.exploredTiles[y]?.[x - 1] ||
            !this.exploredTiles[y]?.[x + 1];

          if (hasUnexploredNeighbor) {
            const px = Math.floor(x * scaleX);
            const py = Math.floor(y * scaleY);
            const pw = Math.max(1, Math.ceil(scaleX));
            const ph = Math.max(1, Math.ceil(scaleY));

            // Lighter fog at edges for smooth transition
            fogCtx.fillStyle = 'rgba(5, 5, 15, 0.3)';
            fogCtx.fillRect(px, py, pw, ph);
          }
        }
      }
    }

    this.fogCacheValid = true;
  }

  /**
   * Draw location markers (towns, castles, caves) - only shows explored locations
   */
  private drawLocations(ctx: CanvasRenderingContext2D): void {
    const scaleX = this.size / this.worldWidth;
    const scaleY = this.size / this.worldHeight;

    for (const location of LOCATIONS) {
      // Skip unexplored locations if fog of war is enabled
      if (this.fogOfWar && !this.isExplored(location.x, location.y)) {
        continue;
      }

      const px = this.screenX + Math.floor(location.x * scaleX);
      const py = this.screenY + Math.floor(location.y * scaleY);

      // Choose color based on location type
      let color: string;
      let size = 2;

      switch (location.type) {
        case LocationType.TOWN:
          color = '#ffcc00'; // Yellow
          size = 2;
          break;
        case LocationType.CASTLE:
          color = '#ff6666'; // Red
          size = 2;
          break;
        case LocationType.DUNGEON:
          color = '#aa66ff'; // Purple
          size = 2;
          break;
        case LocationType.CAVE:
          color = '#888888'; // Gray
          size = 1;
          break;
        case LocationType.SHRINE:
          color = '#66ccff'; // Cyan
          size = 2;
          break;
        default:
          color = '#ffffff'; // White
          size = 1;
      }

      // Draw marker
      ctx.fillStyle = color;
      ctx.fillRect(px, py, size, size);
    }
  }

  /**
   * Draw viewport rectangle showing visible area
   */
  private drawViewport(
    ctx: CanvasRenderingContext2D,
    viewportX: number,
    viewportY: number,
    viewportWidth: number,
    viewportHeight: number
  ): void {
    const tileSize = 16;
    const scaleX = this.size / (this.worldWidth * tileSize);
    const scaleY = this.size / (this.worldHeight * tileSize);

    const vx = this.screenX + viewportX * scaleX;
    const vy = this.screenY + viewportY * scaleY;
    const vw = viewportWidth * scaleX;
    const vh = viewportHeight * scaleY;

    // Draw viewport rectangle outline
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.lineWidth = 1;
    ctx.strokeRect(Math.floor(vx) + 0.5, Math.floor(vy) + 0.5, Math.floor(vw), Math.floor(vh));
  }

  /**
   * Draw player position marker
   */
  private drawPlayer(ctx: CanvasRenderingContext2D, tileX: number, tileY: number): void {
    const scaleX = this.size / this.worldWidth;
    const scaleY = this.size / this.worldHeight;

    const px = this.screenX + Math.floor(tileX * scaleX);
    const py = this.screenY + Math.floor(tileY * scaleY);

    // Draw player marker (3x3 blinking white/yellow dot)
    if (this.playerBlinkOn) {
      // Outer glow
      ctx.fillStyle = 'rgba(255, 255, 100, 0.5)';
      ctx.fillRect(px - 1, py - 1, 4, 4);

      // Inner dot
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(px, py, 2, 2);
    } else {
      // Dimmer state
      ctx.fillStyle = '#ffff80';
      ctx.fillRect(px, py, 2, 2);
    }
  }

  /**
   * Draw border around minimap
   */
  private drawBorder(ctx: CanvasRenderingContext2D): void {
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1;

    // Draw pixel-perfect border
    ctx.strokeRect(
      this.screenX - 0.5,
      this.screenY - 0.5,
      this.size + 1,
      this.size + 1
    );

    // Add corner accents (FF1 style)
    ctx.fillStyle = '#ffffff';
    // Top-left
    ctx.fillRect(this.screenX - 1, this.screenY - 1, 2, 1);
    ctx.fillRect(this.screenX - 1, this.screenY - 1, 1, 2);
    // Top-right
    ctx.fillRect(this.screenX + this.size - 1, this.screenY - 1, 2, 1);
    ctx.fillRect(this.screenX + this.size, this.screenY - 1, 1, 2);
    // Bottom-left
    ctx.fillRect(this.screenX - 1, this.screenY + this.size, 2, 1);
    ctx.fillRect(this.screenX - 1, this.screenY + this.size - 1, 1, 2);
    // Bottom-right
    ctx.fillRect(this.screenX + this.size - 1, this.screenY + this.size, 2, 1);
    ctx.fillRect(this.screenX + this.size, this.screenY + this.size - 1, 1, 2);
  }
}
